// eApp_CategoriesPage.js
import React, { useState, useEffect } from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, Plus, Trash2, GripVertical, User } from 'lucide-react';
import Modal from './eApp_Modal';
import VideoThumbnail from './eApp_VideoThumbnail';

const SortableVideoThumbnail = ({
  video,
  category,
  index,
  onSelectVideo,
  onEditVideo,
  onDeleteVideo,
  onToggleLockVideo,
  onToggleNewVideo,
  lockImageUrl,
}) => {
  const videoId = video.id !== undefined ? video.id : `video-${index}`;
  const uniqueId = `${category}-${videoId}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: uniqueId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative px-2 mb-4 w-1/2 md:w-1/3 lg:w-1/4"
    >
      {/* Drag handle */}
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        className="absolute top-2 left-1/2 transform -translate-x-1/2 cursor-move z-10"
      >
        <GripVertical size={16} className="text-[#666666]" />
      </div>
      <div>
        <VideoThumbnail
          video={video}
          onClick={() => onSelectVideo(video)}
          onEdit={(updatedVideo) => onEditVideo(category, index, updatedVideo)}
          onDelete={() => onDeleteVideo(category, index)}
          onToggleLock={() => onToggleLockVideo(category, index)}
          onToggleNew={() => onToggleNewVideo(category, index)}
          lockImageUrl={lockImageUrl}
        />
      </div>
    </div>
  );
};

const SortableCategory = ({
  category,
  openCategory,
  setOpenCategory,
  onDeleteCategory,
  onSelectVideo,
  onEditVideo,
  onAddVideo,
  onDeleteVideo,
  onToggleLockVideo,
  onToggleNewVideo,
  onReorderVideos,
  lockImageUrl,
  videos,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 'auto',
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="mb-2">
        <div className="relative group">
          <div
            className={`w-full p-4 mb-2 rounded-xl shadow-md transition-all duration-300 ease-in-out ${openCategory === category
                ? 'bg-[#F0EAE2] text-[#333333]'
                : 'bg-white text-[#333333] hover:bg-[#E0E0E0]'
              }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-grow">
                {/* Drag Handle */}
                <div
                  className="mr-2 cursor-move"
                  ref={setActivatorNodeRef}
                  {...listeners}
                  {...attributes}
                >
                  <GripVertical size={20} className="text-[#666666]" />
                </div>
                <span className="text-lg font-semibold">{category}</span>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => onDeleteCategory(category)}
                  className="mr-2 text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={20} />
                </button>
                <button
                  onClick={() =>
                    setOpenCategory(openCategory === category ? null : category)
                  }
                >
                  <ChevronDown
                    className={`transform transition-transform duration-300 ${openCategory === category ? 'rotate-180' : ''
                      }`}
                    size={24}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        {openCategory === category && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 ease-in-out">
            <div className="p-4">
              <SortableContext
                items={videos[category].map((video, idx) => {
                  const videoId = video.id !== undefined ? video.id : `video-${idx}`;
                  return `${category}-${videoId}`;
                })}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap -mx-2">
                  {videos[category].map((video, index) => {
                    const videoId =
                      video.id !== undefined ? video.id : `video-${index}`;
                    return (
                      <SortableVideoThumbnail
                        key={`${category}-${videoId}`}
                        video={video}
                        category={category}
                        index={index}
                        onSelectVideo={onSelectVideo}
                        onEditVideo={onEditVideo}
                        onDeleteVideo={onDeleteVideo}
                        onToggleLockVideo={onToggleLockVideo}
                        onToggleNewVideo={onToggleNewVideo}
                        lockImageUrl={lockImageUrl}
                      />
                    );
                  })}
                </div>
              </SortableContext>
              <button
                onClick={() => onAddVideo(category)}
                className="mt-4 w-full p-2 bg-[#F0EAE2] text-[#333333] rounded-lg flex items-center justify-center hover:bg-[#E0E0E0] transition-colors"
              >
                <Plus size={18} className="mr-2" />
                Add New Video
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoriesPage = ({
  categories,
  videos,
  onSelectVideo,
  onAccountClick,
  onAddVideo,
  onAddCategory,
  onDeleteCategory,
  onReorderCategories,
  onDeleteVideo,
  onToggleLockVideo,
  onToggleNewVideo,
  onReorderVideos,
  lockImageUrl,
}) => {
  const [openCategory, setOpenCategory] = useState(null);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [categoriesState, setCategoriesState] = useState(categories);
  const [videosState, setVideosState] = useState(videos);

  useEffect(() => {
    setCategoriesState(categories);
  }, [categories]);

  useEffect(() => {
    setVideosState(videos);
  }, [videos]);

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      onAddCategory(newCategoryName.trim());
      setNewCategoryName('');
      setIsAddCategoryModalOpen(false);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);

    const { active, over } = event;

    if (over && active.id !== over.id) {
      if (
        categoriesState.includes(active.id) &&
        categoriesState.includes(over.id)
      ) {
        // Reordering categories
        const oldIndex = categoriesState.indexOf(active.id);
        const newIndex = categoriesState.indexOf(over.id);

        const newCategories = arrayMove(categoriesState, oldIndex, newIndex);
        setCategoriesState(newCategories);
        onReorderCategories(newCategories);
      } else {
        // Reordering videos
        const category = categoriesState.find((cat) =>
          videosState[cat].some((video, idx) => {
            const videoId = video.id !== undefined ? video.id : `video-${idx}`;
            return `${cat}-${videoId}` === active.id;
          })
        );

        const videoList = videosState[category];
        const oldIndex = videoList.findIndex((video, idx) => {
          const videoId = video.id !== undefined ? video.id : `video-${idx}`;
          return `${category}-${videoId}` === active.id;
        });
        const newIndex = videoList.findIndex((video, idx) => {
          const videoId = video.id !== undefined ? video.id : `video-${idx}`;
          return `${category}-${videoId}` === over.id;
        });

        if (oldIndex !== -1 && newIndex !== -1) {
          const newVideos = arrayMove(videoList, oldIndex, newIndex);
          const updatedVideosState = {
            ...videosState,
            [category]: newVideos,
          };
          setVideosState(updatedVideosState);
          onReorderVideos(category, newVideos);
        }
      }
    }
  };

  // Updated onEditVideo function
  const onEditVideo = (category, index, updatedVideo) => {
    setVideosState((prevVideos) => {
      const categoryVideos = [...prevVideos[category]];
      const existingVideo = categoryVideos[index];

      const newVideo = {
        ...existingVideo,
        ...updatedVideo,
      };

      categoryVideos[index] = newVideo;

      const newVideosState = {
        ...prevVideos,
        [category]: categoryVideos,
      };

      return newVideosState;
    });
  };

  // Function to render the drag overlay
  const renderDragOverlay = () => {
    if (!activeId) return null;

    // Check if activeId is a category or a video
    if (categoriesState.includes(activeId)) {
      // It's a category
      const category = activeId;
      return (
        <div className="w-full p-4 rounded-xl shadow-md bg-white text-[#333333]">
          <div className="flex items-center">
            <GripVertical size={20} className="mr-2 text-[#666666]" />
            <span className="text-lg font-semibold">{category}</span>
          </div>
        </div>
      );
    } else {
      // It's a video
      const category = categoriesState.find((cat) =>
        videosState[cat].some((video, idx) => {
          const videoId = video.id !== undefined ? video.id : `video-${idx}`;
          return `${cat}-${videoId}` === activeId;
        })
      );

      const video = videosState[category].find((video, idx) => {
        const videoId = video.id !== undefined ? video.id : `video-${idx}`;
        return `${category}-${videoId}` === activeId;
      });

      return (
        <div className="relative px-2 mb-4 w-1/2 md:w-1/3 lg:w-1/4 opacity-75">
          <VideoThumbnail video={video} lockImageUrl={lockImageUrl} />
        </div>
      );
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="p-4 bg-[#FAFAFA] space-y-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-[#333333]">
            Yoga Video Categories
          </h1>
          <button
            onClick={onAccountClick}
            className="flex items-center text-[#333333] hover:text-[#666666] transition-colors"
          >
            <User size={24} className="mr-2" />
            Account
          </button>
        </div>
        <SortableContext
          items={categoriesState}
          strategy={verticalListSortingStrategy}
        >
          <div>
            {categoriesState.map((category) => (
              <SortableCategory
                key={category}
                category={category}
                openCategory={openCategory}
                setOpenCategory={setOpenCategory}
                onDeleteCategory={onDeleteCategory}
                onSelectVideo={onSelectVideo}
                onEditVideo={onEditVideo}
                onAddVideo={onAddVideo}
                onDeleteVideo={onDeleteVideo}
                onToggleLockVideo={onToggleLockVideo}
                onToggleNewVideo={onToggleNewVideo}
                onReorderVideos={onReorderVideos}
                lockImageUrl={lockImageUrl}
                videos={videosState}
              />
            ))}
          </div>
        </SortableContext>
        <button
          onClick={() => setIsAddCategoryModalOpen(true)}
          className="w-full p-4 bg-[#F0EAE2] text-[#333333] rounded-xl flex items-center justify-center hover:bg-[#E0E0E0] transition-colors"
        >
          <Plus size={18} className="mr-2" />
          Add New Category
        </button>
        <Modal
          isOpen={isAddCategoryModalOpen}
          onClose={() => setIsAddCategoryModalOpen(false)}
        >
          <h2 className="text-xl font-bold mb-4">Add New Category</h2>
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Enter category name"
            className="w-full p-2 border border-[#E0E0E0] rounded mb-4"
          />
          <button
            onClick={handleAddCategory}
            className="bg-[#F0EAE2] text-[#333333] py-2 px-4 rounded hover:bg-[#E0E0E0]"
          >
            Add Category
          </button>
        </Modal>
      </div>
      <DragOverlay>{renderDragOverlay()}</DragOverlay>
    </DndContext>
  );
};

export default CategoriesPage;
